import type { ReactNode } from 'react';
import { startTransition, Suspense, useContext, useEffect, useLayoutEffect, useRef } from 'react';

import { ApolloProvider } from '@apollo/client/react';
// Keeping previous comment for context:
// Only import timezones between since 2012 to reduce bundle size
// since we don't care about timezones before 2012.
// https://github.com/js-joda/js-joda/tree/master/packages/timezone#reducing-js-joda-timezone-file-size
//
// 3/10/2023
// changed to 1970-2030, since we're in 2023
// Should we just import everything, how much do we really save?
import '@js-joda/timezone/dist/js-joda-timezone-1970-2030';
import { BricksProvider, ServerSideStyles } from '@opendoor/bricks-next';
import { AddContextualData, addDatadogRUMGlobalContext } from '@opendoor/observability';
import '@opendoor/primer/stylesheets/primer.css';
import faviconPath from '@opendoor/shared-fe/faviconPath';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation } from '@remix-run/react';
import 'core-js/es';
import 'index.css';
import 'intl';
import { AuthProvider } from 'platform/components/AuthContext';
import { LoadingPage } from 'platform/components/LoadingPage';
import { CustomerBoundary } from 'platform/CustomerBoundary';
import { DevToolsCSSReset } from 'platform/DevToolsCSSReset';
import { PageStalenessTimeProvider } from 'platform/hooks/useReloadStalePage';
import { useLoadObservability, useObservability } from 'platform/observability';
import { RemixErrorHandler } from 'platform/RemixErrorHandler';
import { ClientStyleContext } from 'platform/styles/client.context';
import { TabVisibilityTrackerScript } from 'platform/TabVisibilityTrackerScript';
import { apolloClient } from 'setupClient';
import { useTrackInitialLoad } from 'shared/hooks/useTrackPerformance';
import Tamagui from 'tamagui.config';

interface DocumentProps {
  children: ReactNode;
}

const Document = ({ children }: DocumentProps): JSX.Element => {
  const location = useLocation();
  const tamaguiCSS = Tamagui.getCSS();
  const clientStyleData = useContext(ClientStyleContext);
  const reinjectStylesRef = useRef(true);

  // Only executed on client when a top level ErrorBoundary or CatchBoundary are rendered,
  // Using `useLayoutEffect` to render the emotion styles before browser paints the screen.
  useLayoutEffect(() => {
    if (!reinjectStylesRef.current) {
      return;
    }

    // reset cache to re-apply global styles
    clientStyleData.reset();
    // ensure we only do this once per mount
    reinjectStylesRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger on mount/unmount
  }, []);

  useEffect(() => {
    // Wrapped in a startTransition to avoid blocking the main thread
    startTransition(() => {
      addDatadogRUMGlobalContext({ framework: 'remix' });
    });
  }, []);
  useLoadObservability();

  /**
   * Marks when an initial load event occurs
   */
  useTrackInitialLoad();

  return (
    <html lang="en">
      <head>
        <Meta />
        <Links />
        {/**
         * NOTE: The following meta/link tags are included as html tags to ensure
         * they exist on EVERY page. If any of them need to be overriden by individual
         * page meta functions, we can move them into the meta function at the top of
         * this page
         * @see https://remix.run/docs/en/main/route/meta#merging-with-parent-meta
         */}
        <title>Opendoor</title>
        <meta name="framework" content="Remix" />
        <meta charSet="utf-8" />
        <link rel="shortcut icon" href={faviconPath()} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* <!-- Chrome, Firefox OS and Opera --> */}
        <meta name="theme-color" content="#FAF9F6" />
        {/* <!-- Windows Phone --> */}
        <meta name="msapplication-navbutton-color" content="#FAF9F6" />
        {/* <!-- iOS Safari --> */}
        <meta name="apple-mobile-web-app-status-bar-style" content="#FAF9F6" />
        {/* <!--
          manifest.json provides metadata used when your web app is installed on a
          user's mobile device or desktop. See https://developers.google.com/web/fundamentals/web-app-manifest/
        -->
        <link rel="manifest" href="%REACT_APP_PUBLIC_PATH%manifest.json" /> */}
        {/* <!--
          Notice the use of %REACT_APP_PUBLIC_PATH% in the tags above.
          It will be replaced with the URL of the `public` folder during the build.
          Only files inside the `public` folder can be referenced from the HTML.
          Unlike "/favicon.ico" or "favicon.ico", "%REACT_APP_PUBLIC_PATH%favicon.ico" will
          work correctly both with client-side routing and a non-root public URL.
          Learn how to configure a non-root public URL by running `npm run build`.
        --> */}
        {/* <!-- We will prefetch fonts to aid with CLS and LCP --> */}
        {/* A Cloudflare Worker rule redirects consumer.(simplersell|opendoor).com/fonts to the actual font object, so
          do not blindly copy and paste these entries to another application— they might not magically work!
         */}
        <link
          href="/fonts/Graphik-Bold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="/fonts/Graphik-Semibold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="/fonts/Graphik-Medium-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="/fonts/Graphik-Regular-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link rel="canonical" href={location.pathname} />
        <ServerSideStyles tamaguiCSS={tamaguiCSS} />
      </head>
      <body id="root">
        <TabVisibilityTrackerScript />
        {/* eslint-disable-next-line @opendoor/no-html-elements -- QAC|Apptoolbox mount point */}
        <div id="x-od-app-toolbox-root" />
        <AddContextualData data={{ framework: 'remix' }}>
          <ApolloProvider client={apolloClient()}>
            <BricksProvider
              useObservability={useObservability}
              disableInjectCSS
              disableRootThemeClass
            >
              <Suspense fallback={<LoadingPage />}>
                <AuthProvider>{children}</AuthProvider>
              </Suspense>
            </BricksProvider>
          </ApolloProvider>
        </AddContextualData>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
};

const ENV = {
  DEPLOY_ENV: import.meta.env.DEPLOY_ENV as string,
  APP_VERSION: import.meta.env.APP_VERSION as string,
};

// eslint-disable-next-line import-x/no-default-export -- this is how App is supposed to be exported
export default function App(): JSX.Element {
  return (
    <Document>
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(ENV)}`,
        }}
      />
      {/* Injecting CSS only in dev-mode to correct styles that break with primer.css */}
      {ENV.DEPLOY_ENV === 'development' && <DevToolsCSSReset />}
      <CustomerBoundary>
        <PageStalenessTimeProvider>
          <Outlet />
        </PageStalenessTimeProvider>
      </CustomerBoundary>
    </Document>
  );
}

/**
 * Catches errors anywhere in its child component tree and renders a fallback component instead of the component that crashed.
 */
export function ErrorBoundary(): JSX.Element {
  return (
    <Document>
      {/* eslint-disable-next-line src-dir-restricted/remix-error-handler -- root level usage is fine */}
      <RemixErrorHandler />
    </Document>
  );
}
